// components/SessionSetup.tsx
import React, { useState, useEffect } from 'react';
import { useClipShare } from '../hooks/useClipShare';
import { useAlert } from '../hooks/useAlert';
import { useLanguage } from '../context/LanguageContext';

export const SessionSetup: React.FC = () => {
  const { 
    sessionMode, 
    setSessionMode, 
    userNickname, 
    setUserNickname,
    sessionCode,
    setView,
    createSession,
    joinSession
  } = useClipShare();
  
  const { showAlert } = useAlert();
  const { t } = useLanguage();
  const [nickname, setNickname] = useState(userNickname);
  const [joinCode, setJoinCode] = useState('');
  const [showGeneratedCode, setShowGeneratedCode] = useState(false);

  useEffect(() => {
    if (sessionCode && sessionMode === 'host') {
      setShowGeneratedCode(true);
    }
  }, [sessionCode, sessionMode]);

  const handleCreateSession = async () => {
    if (nickname.trim()) {
      setUserNickname(nickname.trim());
    }
    await createSession();
  };

  const handleJoinSession = async () => {
    if (nickname.trim()) {
      setUserNickname(nickname.trim());
    }
    await joinSession(joinCode);
  };

  const handleCopyCode = async () => {
    if (sessionCode) {
      try {
        await navigator.clipboard.writeText(sessionCode);
        showAlert(t.codeCopied, 'success');
      } catch (err) {
        showAlert(t.failedToCopyCode, 'error');
      }
    }
  };

  const handlePasteCode = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const code = text.trim().toUpperCase();
      if (code.length === 6 && /^[A-Z0-9]+$/.test(code)) {
        setJoinCode(code);
        showAlert(t.codeFromClipboard, 'success');
      } else {
        showAlert(t.invalidCodeInClipboard, 'error');
      }
    } catch (err) {
      showAlert(t.failedToReadClipboard, 'error');
    }
  };

  const handleStartChatAsHost = () => {
    setView('chat');
    showAlert(t.chatStartedShare, 'success');
  };

  return (
    <div className="session-setup">
      <div className="setup-card">
        <h2 style={{ marginBottom: '20px' }}>{t.oneTimeSharingTitle}</h2>
        
        <div className="nickname-setup">
          <label style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
            {t.yourNickname}
          </label>
          <input
            type="text"
            className="nickname-input"
            placeholder={t.enterNickname}
            maxLength={20}
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                sessionMode === 'host' ? handleCreateSession() : handleJoinSession();
              }
            }}
          />
          <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginTop: '8px' }}>
            {t.nicknameDesc}
          </p>
        </div>
        
        <div className="mode-selector">
          <button 
            className={`mode-btn ${sessionMode === 'host' ? 'active' : ''}`}
            onClick={() => setSessionMode('host')}
          >
            {t.createSession}
          </button>
          <button 
            className={`mode-btn ${sessionMode === 'join' ? 'active' : ''}`}
            onClick={() => setSessionMode('join')}
          >
            {t.joinSession}
          </button>
        </div>

        {sessionMode === 'host' && (
          <div>
            <p style={{ textAlign: 'center', marginBottom: '20px', color: 'var(--text-secondary)' }}>
              {t.clickToCreateCode}
            </p>
            <button 
              className="btn btn-primary" 
              style={{ width: '100%' }} 
              onClick={handleCreateSession}
            >
              {t.createCode}
            </button>
            
            {showGeneratedCode && sessionCode && (
              <div style={{ marginTop: '20px' }}>
                <div className="code-display">
                  <div className="code-number">{sessionCode}</div>
                  <p>{t.yourSessionCode}</p>
                  <p style={{ fontSize: '0.9rem', opacity: 0.9, marginTop: '10px' }}>
                    {t.shareThisCode}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button 
                    className="btn btn-secondary" 
                    style={{ flex: 1 }} 
                    onClick={handleCopyCode}
                  >
                    {t.copyCode}
                  </button>
                  <button 
                    className="btn btn-primary" 
                    style={{ flex: 1 }} 
                    onClick={handleStartChatAsHost}
                  >
                    {t.startChat}
                  </button>
                </div>
                <p style={{ textAlign: 'center', marginTop: '15px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                  {t.chatAutoOpen}
                </p>
              </div>
            )}
          </div>
        )}

        {sessionMode === 'join' && (
          <div>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 600 }}>
              {t.enterSessionCode}
            </label>
            <input
              type="text"
              className="code-input"
              placeholder="XXXXXX"
              maxLength={6}
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleJoinSession();
                }
              }}
            />
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button 
                className="btn btn-primary" 
                style={{ flex: 1 }} 
                onClick={handleJoinSession}
              >
                {t.connect}
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={handlePasteCode}
              >
                {t.paste}
              </button>
            </div>
          </div>
        )}

        <button 
          className="btn btn-secondary" 
          style={{ width: '100%', marginTop: '20px' }} 
          onClick={() => setView('main')}
        >
          {t.backToMenu}
        </button>
      </div>
    </div>
  );
};